/**
 * Instructor Quiz Builder
 * Component for creating and editing quizzes
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiSave, FiPlus, FiTrash2, FiClock, FiEye, FiX, FiChevronDown, FiChevronUp, FiAlertCircle, FiLoader } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import { useDebouncedTitleValidation } from '../../hooks/useDebouncedTitleValidation';
import DuplicateTitleConfirmModal from '../../components/quizzes/DuplicateTitleConfirmModal';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-2px);
      box-shadow: var(--shadow-colored);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      border-color: var(--border-dark);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const Form = styled.form`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  padding: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &.has-warning {
    border-color: var(--warning-color);
    background: rgba(255, 193, 7, 0.02);
  }

  &.has-warning:focus {
    border-color: var(--warning-color);
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
  }
`;

const TitleInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ValidationIndicator = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--warning-color);
  z-index: 10;
  pointer-events: none;

  svg {
    flex-shrink: 0;

    &.spinner {
      animation: spin 1s linear infinite;

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    }
  }
`;

const ValidationMessage = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid var(--warning-color);
  border-radius: 0;
  color: var(--warning-color);
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 0.25rem;
  }
`;

const PointsInput = styled.input`
  width: 100px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  color: var(--text-primary);
  transition: all 0.3s ease;
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
  }
`;

const ExtraCreditCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--primary-color);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

// const Select = styled.select`
//   width: 100%;
//   padding: 0.75rem;
//   border: 1px solid var(--border-color);
//   border-radius: 0;
//   font-size: 1rem;
//   background-color: var(--background-primary);
//   color: var(--text-primary);
//   transition: var(--transition);

//   &:focus {
//     outline: none;
//     border-color: var(--primary-color);
//     box-shadow: 0 0 0 3px var(--focus-ring);
//   }
// `;


const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);

  &:hover {
    border-color: var(--primary-color);
    background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: var(--primary-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.4;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.75rem;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--primary-color);
    background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.15);
  }
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  accent-color: var(--primary-color);
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`;

const RadioLabel = styled.label`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1.5;

  strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const QuestionsSection = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const QuestionTypeBreakdown = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
  line-height: 1.4;
`;

const QuestionCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 1rem;
  background: var(--background-primary);
  transition: var(--transition);

  &:hover {
    border-color: var(--border-dark);
    box-shadow: var(--shadow-sm);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const QuestionNumber = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-right: 0.75rem;
  display: inline-block;
`;

const QuestionType = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: var(--text-inverse);
  box-shadow: 0 2px 4px rgba(216, 184, 137, 0.3);
`;


const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid var(--border-color);
  border-radius: 0;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
    transform: translateY(-1px);
  }
`;

const QuestionContent = styled.div`
  transition: all 0.3s ease;
  overflow: hidden;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }

  &.danger:hover {
    background: rgba(252, 129, 129, 0.1);
    border-color: var(--error-color);
    color: var(--error-color);
  }
`;

const SubtleDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid var(--error-color);
  background: var(--background-secondary);
  color: var(--error-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-weight: bold;

  &:hover {
    background: rgba(252, 129, 129, 0.1);
    border-color: var(--error-dark);
    color: var(--error-color);
  }
`;

const OptionList = styled.div`
  margin-top: 1.5rem;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  margin-bottom: 0.75rem;
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  min-height: 56px;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);

  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  &:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--background-primary);
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
`;

const OptionTextarea = styled.textarea`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--background-primary);
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  resize: vertical;
  min-height: 48px;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
`;

const OptionText = styled.span`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: var(--text-primary);
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const InstructionText = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
  font-style: italic;
  font-weight: 500;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border-radius: 0;
  border-left: 4px solid var(--primary-color);
`;

const PreviewSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border: 2px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
`;

const PreviewText = styled.div`
  font-size: 1rem;
  color: var(--text-primary);
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin-top: 0.75rem;
  padding: 1rem;
  background: var(--background-primary);
  border: 2px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-sm);
`;

const BlankLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary-color);
  min-width: 80px;
  display: inline-block;
`;

const OptionCheckbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: var(--primary-color);
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const AddOptionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 2px dashed var(--border-color);
  background: linear-gradient(135deg, var(--background-primary) 0%, var(--background-secondary) 100%);
  color: var(--text-secondary);
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.75rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: var(--shadow-sm);

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AddQuestionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border: 2px dashed var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-size: 1.125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: var(--background-secondary);
  }
`;

const CustomDropdown = styled.div`
  position: relative;
  flex: 1;
  z-index: 1;
  
  &:focus-within {
    z-index: 10000;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  background: var(--background-primary);
  cursor: pointer;
  min-height: 48px;
  text-align: left;
  display: flex;
  align-items: center;
  color: ${props => props.hasValue ? 'var(--text-primary)' : 'var(--text-muted)'};
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary);
`;

const DropdownMenu = styled.div.attrs(props => ({
  style: props.position ? {
    position: 'fixed',
    top: `${props.position.top}px`,
    left: `${props.position.left}px`,
    width: `${props.position.width}px`,
  } : {}
}))`
  margin-top: 0.25rem;
  background: var(--background-primary);
  border: 2px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: visible;
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: var(--background-primary);
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-primary);
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-secondary);
  }

  &.selected {
    background: rgba(246, 224, 94, 0.1);
    color: var(--text-primary);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-muted);
`;


function InstructorQuizBuilder() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(quizId);

  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    course_id: null
  });
  const [courses, setCourses] = useState([]);

  // Debug: Log quiz state changes
  useEffect(() => {
    console.log('Quiz state updated:', quiz);
  }, [quiz]);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [collapsedQuestions, setCollapsedQuestions] = useState(new Set());
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = useRef({});

  // Duplicate title validation
  const { isDuplicate, isChecking } = useDebouncedTitleValidation(quiz.title, 500, quizId);
  const [showDuplicateConfirmModal, setShowDuplicateConfirmModal] = useState(false);
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false);

  const fetchQuiz = useCallback(async () => {
    console.log('fetchQuiz called with ID:', quizId);
    try {
      setLoading(true);
      setError(null);
      console.log('Making API call to getQuiz with ID:', quizId);
      const response = await quizApiService.getQuiz(quizId);
      console.log('Quiz API response:', response);
      if (response.success) {
        console.log('Setting quiz data:', response.data);
        setQuiz(response.data);
        setQuestions(response.data.questions || []);
      } else {
        console.error('Failed to fetch quiz:', response.message);
        setError(response.message || 'Failed to fetch quiz');
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError(err.message || 'Failed to fetch quiz');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await quizApiService.getCourses();
      if (response.success) {
        const allCourses = response.courses || response.data || [];
        // Filter to only show active courses that the instructor teaches
        const activeCourses = allCourses.filter(course => 
          course.is_active === 1 || course.is_active === true
        );
        setCourses(activeCourses);
        console.log('Active courses loaded:', activeCourses.length, 'out of', allCourses.length);
        
        // If instructor has only one course, auto-select it
        if (activeCourses.length === 1 && !isEditing) {
          setQuiz(prev => ({
            ...prev,
            course_id: activeCourses[0].id
          }));
          console.log('Auto-selected single course:', activeCourses[0].id);
        }
      } else {
        console.error('Failed to fetch courses:', response.message);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, [isEditing]);

  useEffect(() => {
    console.log('useEffect triggered - isEditing:', isEditing, 'quizId:', quizId);
    fetchCourses();
    if (isEditing) {
      console.log('Calling fetchQuiz for quiz ID:', quizId);
      fetchQuiz();
    }
  }, [quizId, isEditing, fetchQuiz, fetchCourses]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.keys(openDropdowns).some(key => openDropdowns[key])) {
        const target = event.target;
        if (!target.closest('[data-dropdown]')) {
          setOpenDropdowns({});
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);

  const handleQuizChange = (field, value) => {
    setQuiz(prev => ({ ...prev, [field]: value }));
  };

  const addQuestion = (type) => {
    let options = [];
    
    if (type === 'true_false') {
      // Auto-populate True/False options
      options = [
        { option_text: 'True', is_correct: false, order_index: 1 },
        { option_text: 'False', is_correct: false, order_index: 2 }
      ];
    } else if (type !== 'fill_blank' && type !== 'matching') {
      // Multiple choice - empty options
      options = [
        { option_text: '', is_correct: false, order_index: 1 },
        { option_text: '', is_correct: false, order_index: 2 }
      ];
    }
    
    const newQuestion = {
      type,
      prompt: '',
      points: 1,
      order_index: questions.length + 1,
      options,
      settings: type === 'fill_blank' 
        ? { acceptableAnswers: [], caseSensitive: false } 
        : type === 'matching'
        ? { leftItems: [''], rightItems: [''], pairs: [], pointsPerTerm: [1] }
        : type === 'multiple_choice'
        ? { multipleChoiceGradingMode: 'all_required' } // Default to all required
        : null,
      extra_credit: false
    };
    setQuestions(prev => [...prev, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i === index) {
        const updatedQuestion = { ...q, [field]: value };
        
        // Auto-populate answers for fill-in-the-blank questions when prompt changes
        if (field === 'prompt' && q.type === 'fill_blank' && value) {
          const blankCount = value.match(/_{1,}/g)?.length || 0;
          const maxAnswers = blankCount > 0 ? blankCount : 1; // Limit to number of blanks or 1 if no blanks
          const currentAnswers = updatedQuestion.settings.acceptableAnswers.length;
          
          // If we need more answers, add empty answers
          if (currentAnswers < maxAnswers) {
            const newAnswers = [...updatedQuestion.settings.acceptableAnswers];
            while (newAnswers.length < maxAnswers) {
              newAnswers.push('');
            }
            updatedQuestion.settings = { ...updatedQuestion.settings, acceptableAnswers: newAnswers };
          }
          // If we have more answers than allowed, remove excess answers
          else if (currentAnswers > maxAnswers) {
            updatedQuestion.settings = { 
              ...updatedQuestion.settings, 
              acceptableAnswers: updatedQuestion.settings.acceptableAnswers.slice(0, maxAnswers)
            };
          }
        }
        
        // Auto-calculate total points for matching questions when settings change
        if (q.type === 'matching' && field === 'settings' && value) {
          const leftItems = value.leftItems || [];
          const pointsPerTerm = value.pointsPerTerm || [];
          // Count points for all items - use assigned points or default to 1
          const totalPoints = leftItems.reduce((sum, item, itemIndex) => {
            // If points are explicitly assigned, use them (even if 0)
            if (pointsPerTerm[itemIndex] !== undefined && pointsPerTerm[itemIndex] !== null) {
              return sum + (parseFloat(pointsPerTerm[itemIndex]) || 0);
            }
            // If no points assigned but item exists, default to 1
            return sum + 1;
          }, 0);
          updatedQuestion.points = totalPoints;
        }
        
        return updatedQuestion;
      }
      return q;
    }));
  };

  const removeQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const toggleQuestionCollapse = (index) => {
    setCollapsedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Count questions by type
  const getQuestionTypeCounts = () => {
    const counts = {
      multiple_choice: 0,
      true_false: 0,
      fill_blank: 0,
      matching: 0
    };
    
    questions.forEach(question => {
      if (counts.hasOwnProperty(question.type)) {
        counts[question.type]++;
      }
    });
    
    return counts;
  };

  const addOption = (questionIndex) => {
    const question = questions[questionIndex];
    const newOption = {
      option_text: '',
      is_correct: false,
      order_index: question.options.length + 1
    };
    updateQuestion(questionIndex, 'options', [...question.options, newOption]);
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    const question = questions[questionIndex];
    const updatedOptions = question.options.map((opt, i) => 
      i === optionIndex ? { ...opt, [field]: value } : opt
    );
    updateQuestion(questionIndex, 'options', updatedOptions);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const question = questions[questionIndex];
    const updatedOptions = question.options.filter((_, i) => i !== optionIndex);
    updateQuestion(questionIndex, 'options', updatedOptions);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      let saveSuccessful = false;

      // Validate quiz
      if (!quiz.title.trim()) {
        setError('Quiz title is required');
        setSaving(false);
        return;
      }

      if (!quiz.course_id) {
        setError('Please select a course');
        setSaving(false);
        return;
      }

      if (questions.length === 0) {
        setError('At least one question is required');
        setSaving(false);
        return;
      }

      // Check for duplicate title
      if (isDuplicate && !proceedWithDuplicate) {
        setSaving(false);
        setShowDuplicateConfirmModal(true);
        return;
      }

      // Validate questions
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.prompt.trim()) {
          setError(`Question ${i + 1} prompt is required`);
          return;
        }
        if (q.type !== 'fill_blank' && q.type !== 'matching' && q.options.length < 2) {
          setError(`Question ${i + 1} must have at least 2 options`);
          return;
        }
        if (q.type !== 'fill_blank' && q.type !== 'matching') {
          const hasCorrect = q.options.some(opt => opt.is_correct);
          if (!hasCorrect) {
            setError(`Question ${i + 1} must have at least one correct answer`);
            return;
          }
        }
        if (q.type === 'fill_blank' && (!q.settings.acceptableAnswers || q.settings.acceptableAnswers.length === 0)) {
          setError(`Question ${i + 1} must have at least one acceptable answer`);
          return;
        }
        if (q.type === 'matching') {
          const leftItems = q.settings?.leftItems || [];
          const rightItems = q.settings?.rightItems || [];
          const pairs = q.settings?.pairs || [];
          const pointsPerTerm = q.settings?.pointsPerTerm || [];
          // Check for at least 1 term and 1 definition
          const filledLeftItems = leftItems.filter(item => item.trim());
          const filledRightItems = rightItems.filter(item => item.trim());
          if (filledLeftItems.length < 1) {
            setError(`Question ${i + 1} must have at least 1 term`);
            return;
          }
          if (filledRightItems.length < 1) {
            setError(`Question ${i + 1} must have at least 1 definition`);
            return;
          }
          if (pairs.length !== filledLeftItems.length) {
            setError(`Question ${i + 1} must have all terms paired correctly`);
            return;
          }
          // Validate that all terms have points set
          for (let j = 0; j < leftItems.length; j++) {
            if (leftItems[j].trim() && (pointsPerTerm[j] === undefined || pointsPerTerm[j] <= 0)) {
              setError(`Question ${i + 1}: Each term must have points greater than 0`);
              return;
            }
          }
        }
      }

      // Update order indices
      const orderedQuestions = questions.map((q, index) => ({
        ...q,
        order_index: index + 1,
        options: q.options.map((opt, optIndex) => ({
          ...opt,
          order_index: optIndex + 1
        }))
      }));

      if (isEditing) {
        // Update quiz title and description first
        const quizData = {
          title: quiz.title.trim(),
          description: quiz.description?.trim() || null,
          course_id: quiz.course_id || null
        };
        
        // Update quiz basic info
        await quizApiService.updateQuiz(quizId, quizData);
        
        // Update questions
        await quizApiService.updateQuizQuestions(quizId, orderedQuestions);
        saveSuccessful = true;
      } else {
        // Prepare quiz data for API
        const quizData = {
          title: quiz.title.trim(),
          description: quiz.description?.trim() || null,
          course_id: quiz.course_id || null
        };
        
        console.log('Sending quiz data:', quizData);
        
        let response;
        try {
          response = await quizApiService.createQuiz(quizData);
          console.log('Quiz creation response:', response);
        } catch (createError) {
          console.error('Error calling createQuiz API:', createError);
          console.error('Error details:', {
            message: createError.message,
            status: createError.status,
            errorData: createError.errorData || createError.data,
            stack: createError.stack
          });
          throw createError; // Re-throw to be caught by outer catch
        }
        
        if (response && response.success) {
          const newQuizId = response.data?.id;
          console.log('Quiz created successfully, ID:', newQuizId);
          
          if (!newQuizId) {
            console.error('No quiz ID returned from creation');
            setError('Quiz was created but no ID was returned. Please refresh and try again.');
            setSaving(false);
            return;
          }
          
          // Update questions
          console.log('Updating questions for quiz:', newQuizId, 'Questions:', orderedQuestions);
          try {
            const questionsResponse = await quizApiService.updateQuizQuestions(newQuizId, orderedQuestions);
            console.log('Questions update response:', questionsResponse);
            
            if (!questionsResponse || !questionsResponse.success) {
              console.error('Questions update failed:', questionsResponse);
              setError('Quiz was created but questions failed to save. Please edit the quiz to add questions.');
              setSaving(false);
              return; // Don't navigate if questions failed
            }
            
            // If we get here, everything succeeded
            saveSuccessful = true;
          } catch (questionsError) {
            console.error('Error updating questions:', questionsError);
            setError('Quiz was created but questions failed to save: ' + (questionsError.message || 'Unknown error'));
            setSaving(false);
            return; // Don't navigate if questions failed
          }
        } else {
          console.error('Quiz creation failed:', response);
          // Handle validation errors
          if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
            const errorMessages = response.errors.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
            setError(`Validation failed: ${errorMessages}`);
          } else {
            setError(response?.message || 'Failed to create quiz');
          }
          setSaving(false);
          return;
        }
      }

      // Only navigate if we successfully saved everything
      if (saveSuccessful) {
        console.log('Quiz saved successfully, navigating to quizzes list');
        setProceedWithDuplicate(false); // Reset state
        navigate('/instructor/quizzes');
      } else {
        console.log('Save was not successful, not navigating');
      }

    } catch (err) {
      console.error('Error saving quiz:', err);
      // Handle API errors with validation details
      const errorData = err.errorData || err.data;
      if (err.status === 400 && errorData) {
        // Validation errors from API
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          setError(`Validation failed: ${errorMessages}`);
        } else {
          setError(errorData.message || err.message || 'Validation failed');
        }
      } else {
        setError('Failed to save quiz: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setSaving(false);
      setProceedWithDuplicate(false); // Reset state
    }
  };

  const handleConfirmDuplicate = () => {
    setProceedWithDuplicate(true);
    setShowDuplicateConfirmModal(false);
    // Re-trigger save with proceedWithDuplicate flag
    setTimeout(() => {
      handleSave();
    }, 0);
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateConfirmModal(false);
    setProceedWithDuplicate(false);
  };


  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={24} />
          <p>Loading quiz...</p>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{isEditing ? 'Edit Quiz' : 'Create Quiz'}</Title>
        <ButtonGroup>
          <Button className="secondary" onClick={() => navigate('/instructor/quizzes')}>
            Cancel
          </Button>
          {isEditing && (
            <Button className="secondary" onClick={() => navigate(`/instructor/quizzes/${quizId}`)}>
              <FiEye size={16} />
              Preview Quiz
            </Button>
          )}
          <Button className="primary" onClick={handleSave} disabled={saving}>
            <FiSave size={16} />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonGroup>
      </Header>

      {error && (
        <div style={{ 
          background: 'rgba(252, 129, 129, 0.1)', 
          color: 'var(--error-color)', 
          padding: '1rem', 
          borderRadius: 0, 
          marginBottom: '2rem' 
        }}>
          {error}
        </div>
      )}

      <Form>

        <FormGroup>
          <Label htmlFor="title">Quiz Title *</Label>
          <TitleInputWrapper>
            <Input
              id="title"
              type="text"
              value={quiz.title}
              onChange={(e) => handleQuizChange('title', e.target.value)}
              placeholder="Enter quiz title"
              className={isDuplicate ? 'has-warning' : ''}
            />
            {isChecking && (
              <ValidationIndicator>
                <FiLoader size={16} className="spinner" />
              </ValidationIndicator>
            )}
            {isDuplicate && !isChecking && (
              <ValidationIndicator>
                <FiAlertCircle size={16} />
              </ValidationIndicator>
            )}
          </TitleInputWrapper>
          {isDuplicate && (
            <ValidationMessage>
              <FiAlertCircle size={16} />
              <div>
                <strong>Title Already Exists</strong>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                  A quiz with this title already exists. You can change the title or proceed anyway.
                </p>
              </div>
            </ValidationMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="course_id">Course *</Label>
          <select
            id="course_id"
            value={quiz.course_id || ''}
            onChange={(e) => handleQuizChange('course_id', e.target.value ? parseInt(e.target.value) : null)}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              backgroundColor: 'var(--background-primary)',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
          >
            <option value="">Select course</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name}
              </option>
            ))}
          </select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <TextArea
            id="description"
            value={quiz.description || ''}
            onChange={(e) => handleQuizChange('description', e.target.value)}
            placeholder="Enter quiz description"
          />
        </FormGroup>


      </Form>

      <QuestionsSection>
        <SectionHeader>
          <div>
            <SectionTitle>Total Questions ({questions.length})</SectionTitle>
            <QuestionTypeBreakdown>
              {(() => {
                const counts = getQuestionTypeCounts();
                const breakdown = [];
                if (counts.multiple_choice > 0) breakdown.push(`${counts.multiple_choice} Multiple Choice`);
                if (counts.true_false > 0) breakdown.push(`${counts.true_false} True/False`);
                if (counts.fill_blank > 0) breakdown.push(`${counts.fill_blank} Fill in the Blank`);
                if (counts.matching > 0) breakdown.push(`${counts.matching} Matching`);
                return breakdown.length > 0 ? breakdown.join(' • ') : 'No questions added yet';
              })()}
            </QuestionTypeBreakdown>
          </div>
        </SectionHeader>

        {questions.map((question, index) => (
          <QuestionCard key={index}>
            <QuestionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <CollapseButton onClick={() => toggleQuestionCollapse(index)}>
                  {collapsedQuestions.has(index) ? <FiChevronDown size={16} /> : <FiChevronUp size={16} />}
                </CollapseButton>
                <QuestionNumber>Question {index + 1}</QuestionNumber>
                <QuestionType>
                  {question.type === 'true_false' ? 'true/false' : 
                   question.type === 'fill_blank' ? 'fill in the blank' : 
                   question.type === 'matching' ? 'matching' :
                   question.type.replace('_', ' ')}
                </QuestionType>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {question.type !== 'matching' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Points:</span>
                    <PointsInput
                      type="number"
                      value={question.points || 1}
                      onChange={(e) => updateQuestion(index, 'points', parseFloat(e.target.value))}
                      min="0.1"
                      max="100"
                      step="0.1"
                      style={{ width: '90px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  )}
                  {question.type === 'matching' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                        Total Points: {(() => {
                          const leftItems = question.settings?.leftItems || [];
                          const pointsPerTerm = question.settings?.pointsPerTerm || [];
                          // Count points for all items - use assigned points or default to 1
                          const total = leftItems.reduce((sum, item, itemIndex) => {
                            // If points are explicitly assigned, use them (even if 0)
                            if (pointsPerTerm[itemIndex] !== undefined && pointsPerTerm[itemIndex] !== null) {
                              return sum + (parseFloat(pointsPerTerm[itemIndex]) || 0);
                            }
                            // If no points assigned but item exists, default to 1
                            return sum + 1;
                          }, 0);
                          return total;
                        })()}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Extra Credit:</span>
                    <ExtraCreditCheckbox
                      type="checkbox"
                      checked={question.extra_credit || false}
                      onChange={(e) => updateQuestion(index, 'extra_credit', e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </div>
                </div>
                <ActionButton onClick={() => removeQuestion(index)} className="danger">
                  <FiTrash2 size={14} />
                </ActionButton>
              </div>
            </QuestionHeader>

            {!collapsedQuestions.has(index) && (
              <QuestionContent>
                {question.type === 'fill_blank' && (
              <OptionList>
                <InstructionText>
                  <strong>Type _ (single underscore) where you want students to fill in the blank. Questions without underscores are also acceptable.</strong><br />
                  For example: ICN stands for Islamic Center of _ . It is located in the city of _ , Illinois.
                </InstructionText>
                
                {question.prompt && (
                  <PreviewSection>
                    <Label>Preview (how students will see it):</Label>
                    <PreviewText>
                      {question.prompt.replace(/_{1,}/g, '________')}
                    </PreviewText>
                  </PreviewSection>
                )}
              </OptionList>
            )}

            {question.type === 'matching' && (
              <OptionList>
                <InstructionText>
                  <strong>Create a matching question by entering corresponding terms and definitions.</strong>
                </InstructionText>
              </OptionList>
            )}

            <FormGroup>
              <Label>Question Prompt *</Label>
              <TextArea
                value={question.prompt}
                onChange={(e) => updateQuestion(index, 'prompt', e.target.value)}
                placeholder="Enter your question..."
              />
            </FormGroup>


            {question.type === 'multiple_choice' && (
              <>
                <OptionList>
                  <Label>Options</Label>
                  <InstructionText>Check one or more boxes that are correct</InstructionText>
                  {question.options.map((option, optIndex) => (
                    <OptionItem key={optIndex}>
                      <OptionCheckbox
                        type="checkbox"
                        checked={option.is_correct}
                        onChange={(e) => updateOption(index, optIndex, 'is_correct', e.target.checked)}
                      />
                      <OptionInput
                        value={option.option_text}
                        onChange={(e) => updateOption(index, optIndex, 'option_text', e.target.value)}
                        placeholder="Enter option text"
                      />
                      <SubtleDeleteButton onClick={() => removeOption(index, optIndex)}>
                        <FiX size={12} />
                      </SubtleDeleteButton>
                    </OptionItem>
                  ))}
                  <AddOptionButton onClick={() => addOption(index)}>
                    <FiPlus size={14} />
                    Add Option
                  </AddOptionButton>
                </OptionList>
                
                {/* Advanced option: Show only when there are multiple correct answers */}
                {question.options.filter(opt => opt.is_correct).length > 1 && (
                  <FormGroup style={{ marginTop: '1.5rem' }}>
                    <Label>Advanced Grading Options</Label>
                    <InstructionText>When multiple answers are correct, how should students be graded?</InstructionText>
                    <RadioGroup>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name={`mc_grading_${index}`}
                          value="all_required"
                          checked={(question.settings?.multipleChoiceGradingMode || 'all_required') === 'all_required'}
                          onChange={(e) => {
                            const updatedQuestion = { ...question };
                            updatedQuestion.settings = {
                              ...updatedQuestion.settings,
                              multipleChoiceGradingMode: 'all_required'
                            };
                            updateQuestion(index, 'settings', updatedQuestion.settings);
                          }}
                        />
                        <RadioLabel>
                          <strong>Student must select ALL correct answers</strong>
                        </RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name={`mc_grading_${index}`}
                          value="any_correct"
                          checked={question.settings?.multipleChoiceGradingMode === 'any_correct'}
                          onChange={(e) => {
                            const updatedQuestion = { ...question };
                            updatedQuestion.settings = {
                              ...updatedQuestion.settings,
                              multipleChoiceGradingMode: 'any_correct'
                            };
                            updateQuestion(index, 'settings', updatedQuestion.settings);
                          }}
                        />
                        <RadioLabel>
                          <strong>Student can select any of the correct answers</strong>
                        </RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </FormGroup>
                )}
              </>
            )}

            {question.type === 'true_false' && (
              <OptionList>
                <InstructionText>Choose whether the statement is True or False</InstructionText>
                {question.options.map((option, optIndex) => (
                  <OptionItem key={optIndex}>
                    <OptionCheckbox
                      type="radio"
                      name={`tf_${index}`}
                      checked={option.is_correct}
                      onChange={() => {
                        // Set all options to false first
                        const updatedOptions = question.options.map(opt => ({ ...opt, is_correct: false }));
                        // Set the selected one to true
                        updatedOptions[optIndex].is_correct = true;
                        updateQuestion(index, 'options', updatedOptions);
                      }}
                    />
                    <OptionText>{option.option_text}</OptionText>
                  </OptionItem>
                ))}
              </OptionList>
            )}

            {question.type === 'fill_blank' && (
              <OptionList>
                <Label>Acceptable Answers</Label>
                <InstructionText>
                  {question.prompt && question.prompt.match(/_{1,}/g)?.length > 1 
                    ? `Answer fields automatically created for ${question.prompt.match(/_{1,}/g)?.length} blanks. Enter answers in order. For multiple correct answers per blank, separate with commas.`
                    : question.prompt && question.prompt.match(/_{1,}/g)?.length === 1
                    ? 'Answer field automatically created for 1 blank.'
                    : ''
                  }
                </InstructionText>
                {question.settings.acceptableAnswers.map((answer, ansIndex) => {
                  const blankCount = question.prompt ? question.prompt.match(/_{1,}/g)?.length || 0 : 0;
                  const maxAnswers = blankCount > 0 ? blankCount : 1; // Limit to number of blanks or 1 if no blanks
                  const blankNumber = ansIndex + 1;
                  // const isMultipleBlanks = blankCount > 1;
                  const hasBlanks = blankCount > 0;
                  
                  // Only show answer boxes up to the limit
                  if (ansIndex >= maxAnswers) return null;
                  
                  return (
                    <OptionItem key={ansIndex}>
                      {hasBlanks ? (
                        <BlankLabel>Blank {blankNumber}:</BlankLabel>
                      ) : (
                        <BlankLabel>Answer:</BlankLabel>
                      )}
                      <OptionInput
                        value={answer}
                        onChange={(e) => {
                          const newAnswers = [...question.settings.acceptableAnswers];
                          newAnswers[ansIndex] = e.target.value;
                          updateQuestion(index, 'settings', { ...question.settings, acceptableAnswers: newAnswers });
                        }}
                        placeholder={hasBlanks ? `Enter answers for blank ${blankNumber} (separate multiple with commas)` : "Enter correct answers (separate multiple with commas)"}
                      />
                      {/* Only show delete button if we have more than the minimum required */}
                      {question.settings.acceptableAnswers.length > maxAnswers && (
                        <SubtleDeleteButton onClick={() => {
                          const newAnswers = question.settings.acceptableAnswers.filter((_, i) => i !== ansIndex);
                          updateQuestion(index, 'settings', { ...question.settings, acceptableAnswers: newAnswers });
                        }}>
                          <FiX size={12} />
                        </SubtleDeleteButton>
                      )}
                    </OptionItem>
                  );
                })}
                {(() => {
                  const blankCount = question.prompt ? question.prompt.match(/_{1,}/g)?.length || 0 : 0;
                  const maxAnswers = blankCount > 0 ? blankCount : 1;
                  const currentAnswers = question.settings.acceptableAnswers.length;
                  
                  // Only show Add button if we haven't reached the limit
                  if (currentAnswers >= maxAnswers) return null;
                  
                  return (
                    <AddOptionButton onClick={() => {
                      const newAnswers = [...question.settings.acceptableAnswers, ''];
                      updateQuestion(index, 'settings', { ...question.settings, acceptableAnswers: newAnswers });
                    }}>
                      <FiPlus size={14} />
                      {blankCount === 0 ? 'Add Answer' : `Add Answer ${currentAnswers + 1}`}
                    </AddOptionButton>
                  );
                })()}
                <CheckboxGroup style={{ marginTop: '1rem' }}>
                  <Checkbox
                    type="checkbox"
                    checked={question.settings.caseSensitive}
                    onChange={(e) => updateQuestion(index, 'settings', { ...question.settings, caseSensitive: e.target.checked })}
                  />
                  <CheckboxLabel>Case sensitive</CheckboxLabel>
                </CheckboxGroup>
              </OptionList>
            )}

            {question.type === 'matching' && (
              <OptionList>
                <Label>Matching Items</Label>
                <InstructionText>
                  Add items to the left list (terms) and right list (definitions).
                </InstructionText>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
                  {/* Left Items */}
                  <div>
                    <Label style={{ marginBottom: '0.75rem' }}>List of Terms</Label>
                    {question.settings.leftItems.map((item, itemIndex) => {
                      const pointsPerTerm = question.settings.pointsPerTerm || [];
                      // Default to 1 if not set, or use the value from array
                      const termPoints = pointsPerTerm[itemIndex] !== undefined && pointsPerTerm[itemIndex] !== null
                        ? pointsPerTerm[itemIndex]
                        : 1;
                      
                      return (
                        <div key={itemIndex} style={{ marginBottom: '0.75rem' }}>
                          <OptionItem>
                            <OptionTextarea
                              value={item}
                              onChange={(e) => {
                                const newLeftItems = [...question.settings.leftItems];
                                newLeftItems[itemIndex] = e.target.value;
                                updateQuestion(index, 'settings', { ...question.settings, leftItems: newLeftItems });
                              }}
                              placeholder={`Term ${itemIndex + 1}`}
                              style={{ flex: 1 }}
                              rows={1}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '120px' }}>
                              <Label style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Points:</Label>
                              <PointsInput
                                type="number"
                                value={termPoints}
                                onChange={(e) => {
                                  const newPointsPerTerm = [...(question.settings.pointsPerTerm || [])];
                                  while (newPointsPerTerm.length <= itemIndex) {
                                    newPointsPerTerm.push(1);
                                  }
                                  newPointsPerTerm[itemIndex] = parseFloat(e.target.value) || 0;
                                  updateQuestion(index, 'settings', { ...question.settings, pointsPerTerm: newPointsPerTerm });
                                }}
                                min="0"
                                max="100"
                                step="0.1"
                                style={{ width: '70px', padding: '0.5rem', fontSize: '0.875rem' }}
                              />
                            </div>
                            {(() => {
                              // Only show delete button if there's more than 1 filled term
                              const filledLeftItems = question.settings.leftItems.filter(item => item.trim());
                              return filledLeftItems.length > 1 && (
                                <SubtleDeleteButton onClick={() => {
                                  const newLeftItems = question.settings.leftItems.filter((_, i) => i !== itemIndex);
                                  const newPointsPerTerm = (question.settings.pointsPerTerm || []).filter((_, i) => i !== itemIndex);
                                  // Remove corresponding pair if it exists
                                  const newPairs = question.settings.pairs.filter(p => p.leftIndex !== itemIndex)
                                    .map(p => p.leftIndex > itemIndex ? { ...p, leftIndex: p.leftIndex - 1 } : p);
                                  updateQuestion(index, 'settings', { 
                                    ...question.settings, 
                                    leftItems: newLeftItems,
                                    pointsPerTerm: newPointsPerTerm,
                                    pairs: newPairs
                                  });
                                }}>
                                  <FiX size={12} />
                                </SubtleDeleteButton>
                              );
                            })()}
                          </OptionItem>
                        </div>
                      );
                    })}
                    <AddOptionButton onClick={() => {
                      const newLeftItems = [...question.settings.leftItems, ''];
                      const newPointsPerTerm = [...(question.settings.pointsPerTerm || []), 1];
                      updateQuestion(index, 'settings', { ...question.settings, leftItems: newLeftItems, pointsPerTerm: newPointsPerTerm });
                    }}>
                      <FiPlus size={14} />
                      Add Term
                    </AddOptionButton>
                  </div>

                  {/* Right Items */}
                  <div>
                    <Label style={{ marginBottom: '0.75rem' }}>List of Definitions</Label>
                    {question.settings.rightItems.map((item, itemIndex) => (
                      <OptionItem key={itemIndex} style={{ marginBottom: '0.75rem' }}>
                        <OptionTextarea
                          value={item}
                          onChange={(e) => {
                            const newRightItems = [...question.settings.rightItems];
                            newRightItems[itemIndex] = e.target.value;
                            updateQuestion(index, 'settings', { ...question.settings, rightItems: newRightItems });
                          }}
                          placeholder={`Definition ${itemIndex + 1}`}
                          rows={1}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                        />
                        {(() => {
                          // Only show delete button if there's more than 1 filled definition
                          const filledRightItems = question.settings.rightItems.filter(item => item.trim());
                          return filledRightItems.length > 1 && (
                            <SubtleDeleteButton onClick={() => {
                              const newRightItems = question.settings.rightItems.filter((_, i) => i !== itemIndex);
                              // Remove corresponding pair if it exists
                              const newPairs = question.settings.pairs.filter(p => p.rightIndex !== itemIndex)
                                .map(p => p.rightIndex > itemIndex ? { ...p, rightIndex: p.rightIndex - 1 } : p);
                              updateQuestion(index, 'settings', { 
                                ...question.settings, 
                                rightItems: newRightItems,
                                pairs: newPairs
                              });
                            }}>
                              <FiX size={12} />
                            </SubtleDeleteButton>
                          );
                        })()}
                      </OptionItem>
                    ))}
                    <AddOptionButton onClick={() => {
                      const newRightItems = [...question.settings.rightItems, ''];
                      updateQuestion(index, 'settings', { ...question.settings, rightItems: newRightItems });
                    }}>
                      <FiPlus size={14} />
                      Add Definition
                    </AddOptionButton>
                  </div>
                </div>

                {/* Pairing Section */}
                {question.settings.leftItems.some(item => item.trim()) && question.settings.rightItems.some(item => item.trim()) && (
                  <>
                    <OptionList style={{ marginTop: '2rem' }}>
                      <Label>Pair Items</Label>
                      <InstructionText>
                        Match each left term with its correct definition
                      </InstructionText>
                    </OptionList>
                    <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: 'linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%)', borderRadius: 0, border: '2px solid var(--border-color)', position: 'relative', overflow: 'visible' }}>
                      {question.settings.leftItems.map((leftItem, leftIndex) => {
                      if (!leftItem.trim()) return null;
                      
                      const currentPair = question.settings.pairs.find(p => p.leftIndex === leftIndex);
                      const currentRightIndex = currentPair ? currentPair.rightIndex : null;
                      const pointsPerTerm = question.settings.pointsPerTerm || [];
                      const termPoints = pointsPerTerm[leftIndex] !== undefined ? pointsPerTerm[leftIndex] : 1;
                      
                      return (
                        <div key={leftIndex} style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ flex: '0 0 200px', fontWeight: '600', color: 'var(--text-primary)', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {leftItem} ({termPoints} pt{termPoints !== 1 ? 's' : ''}) →
                          </span>
                          <CustomDropdown 
                            data-dropdown
                            ref={el => {
                              const dropdownKey = `${index}-${leftIndex}`;
                              dropdownRefs.current[dropdownKey] = el;
                            }}
                          >
                            <DropdownButton
                              type="button"
                              hasValue={currentRightIndex !== null}
                              onClick={() => {
                                const dropdownKey = `${index}-${leftIndex}`;
                                const button = dropdownRefs.current[dropdownKey]?.querySelector('button');
                                if (button) {
                                  const rect = button.getBoundingClientRect();
                                  setDropdownPositions(prev => ({
                                    ...prev,
                                    [dropdownKey]: {
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.left + window.scrollX,
                                      width: rect.width
                                    }
                                  }));
                                }
                                setOpenDropdowns(prev => ({
                                  ...prev,
                                  [dropdownKey]: !prev[dropdownKey]
                                }));
                              }}
                            >
                              {currentRightIndex !== null 
                                ? (question.settings.rightItems[currentRightIndex] || `Definition ${currentRightIndex + 1}`)
                                : 'Select match...'}
                              <DropdownIcon>
                                {openDropdowns[`${index}-${leftIndex}`] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                              </DropdownIcon>
                            </DropdownButton>
                            {openDropdowns[`${index}-${leftIndex}`] && createPortal(
                              <DropdownMenu 
                                data-dropdown
                                position={dropdownPositions[`${index}-${leftIndex}`]}
                              >
                                <DropdownOption
                                  type="button"
                                  onClick={() => {
                                    const newPairs = question.settings.pairs.filter(p => p.leftIndex !== leftIndex);
                                    updateQuestion(index, 'settings', { ...question.settings, pairs: newPairs });
                                    setOpenDropdowns(prev => {
                                      const newState = { ...prev };
                                      delete newState[`${index}-${leftIndex}`];
                                      return newState;
                                    });
                                  }}
                                >
                                  {currentRightIndex !== null ? 'Clear selection' : 'Select match...'}
                                </DropdownOption>
                                {question.settings.rightItems.map((rightItem, rightIndex) => (
                                  <DropdownOption
                                    key={rightIndex}
                                    type="button"
                                    className={currentRightIndex === rightIndex ? 'selected' : ''}
                                    onClick={() => {
                                      const newPairs = question.settings.pairs.filter(p => p.leftIndex !== leftIndex);
                                      newPairs.push({ leftIndex, rightIndex });
                                      updateQuestion(index, 'settings', { ...question.settings, pairs: newPairs });
                                      setOpenDropdowns(prev => {
                                        const newState = { ...prev };
                                        delete newState[`${index}-${leftIndex}`];
                                        return newState;
                                      });
                                    }}
                                  >
                                    {rightItem || `Definition ${rightIndex + 1}`}
                                  </DropdownOption>
                                ))}
                              </DropdownMenu>,
                              document.body
                            )}
                          </CustomDropdown>
                        </div>
                      );
                    })}
                    </div>
                  </>
                )}
              </OptionList>
            )}
              </QuestionContent>
            )}
          </QuestionCard>
        ))}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <AddQuestionButton onClick={() => addQuestion('multiple_choice')}>
            <FiPlus size={16} />
            Add Multiple Choice
          </AddQuestionButton>
          <AddQuestionButton onClick={() => addQuestion('true_false')}>
            <FiPlus size={16} />
            Add True/False
          </AddQuestionButton>
          <AddQuestionButton onClick={() => addQuestion('fill_blank')}>
            <FiPlus size={16} />
            Add Fill in the Blank
          </AddQuestionButton>
          <AddQuestionButton onClick={() => addQuestion('matching')}>
            <FiPlus size={16} />
            Add Matching
          </AddQuestionButton>
        </div>
      </QuestionsSection>

      {showDuplicateConfirmModal && (
        <DuplicateTitleConfirmModal
          title={quiz.title}
          onConfirm={handleConfirmDuplicate}
          onCancel={handleCancelDuplicate}
          isLoading={saving}
        />
      )}
    </Container>
  );
}

export default InstructorQuizBuilder;
